import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { API_URL } from '../config/api';

interface TranslatedKeyword {
  keyword: string;
  isExisting: boolean;
  languageCode: string;
}

interface TranslatedLanguage {
  flag: string;
  name: string;
  keywords: TranslatedKeyword[];
}

interface TranslatedKeywordsModalProps {
  isOpen: boolean;
  onClose: () => void;
  keyword: string;
  onKeywordsAdded: () => void;
}

const TranslatedKeywordsModal: React.FC<TranslatedKeywordsModalProps> = ({
  isOpen,
  onClose,
  keyword,
  onKeywordsAdded
}) => {
  const [translations, setTranslations] = useState<Record<string, TranslatedLanguage>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addingKeywords, setAddingKeywords] = useState<Set<string>>(new Set());
  const [addedKeywords, setAddedKeywords] = useState<Set<string>>(new Set());
  const [stats, setStats] = useState<{ total: number; new: number; existing: number } | null>(null);

  useEffect(() => {
    if (isOpen && keyword) {
      fetchTranslations();
    }
  }, [isOpen, keyword]);

  // Language flag and name mapping
  const getLanguageInfo = (languageName: string): { flag: string; name: string } => {
    const langMap: Record<string, { flag: string; name: string }> = {
      'english': { flag: '🇺🇸', name: 'English' },
      'español': { flag: '🇪🇸', name: 'Spanish' }, 
      'português': { flag: '🇧🇷', name: 'Portuguese' },
      'русский': { flag: '🇷🇺', name: 'Russian' },
      '日本語': { flag: '🇯🇵', name: 'Japanese' },
      'deutsch': { flag: '🇩🇪', name: 'German' },
      'français': { flag: '🇫🇷', name: 'French' },
      'italiano': { flag: '🇮🇹', name: 'Italian' },
      'nederlands': { flag: '🇳🇱', name: 'Dutch' },
      'polski': { flag: '🇵🇱', name: 'Polish' },
      'svenska': { flag: '🇸🇪', name: 'Swedish' },
      '한국어': { flag: '🇰🇷', name: 'Korean' }
    };
    return langMap[languageName] || { flag: '🌐', name: languageName };
  };

  const fetchTranslations = async () => {
    setLoading(true);
    setError(null);
    setTranslations({});
    setAddedKeywords(new Set());
    
    try {
      const response = await fetch(`${API_URL}/api/keywords/translate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ keyword }),
      });

      const data = await response.json();
      
      if (data.success) {
        // Update flags and names for each language
        const updatedData = { ...data.data };
        Object.keys(updatedData).forEach(langName => {
          const langInfo = getLanguageInfo(langName);
          updatedData[langName] = {
            ...updatedData[langName],
            flag: langInfo.flag,
            name: langInfo.name
          };
        });
        
        setTranslations(updatedData);
        setStats(data.stats);
      } else {
        setError(data.error || 'Failed to fetch translations');
      }
    } catch (err) {
      console.error('Error fetching translations:', err);
      setError('Failed to fetch translations');
    } finally {
      setLoading(false);
    }
  };

  const addKeyword = async (keywordToAdd: string, languageCode?: string) => {
    setAddingKeywords(prev => new Set(prev).add(keywordToAdd));
    
    try {
      const response = await fetch(`${API_URL}/api/keywords`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          keyword: keywordToAdd.toLowerCase().replace(/\s+/g, ''),
          language: languageCode || 'en'
        }),
      });

      const data = await response.json();
      
      if (data.success) {
        setAddedKeywords(prev => new Set(prev).add(keywordToAdd));
        // Update the isExisting flag in translations
        setTranslations(prev => {
          const updated = { ...prev };
          Object.keys(updated).forEach(lang => {
            updated[lang] = {
              ...updated[lang],
              keywords: updated[lang].keywords.map(k =>
                k.keyword === keywordToAdd
                  ? { ...k, isExisting: true }
                  : k
              )
            };
          });
          return updated;
        });
        
        // Update stats
        if (stats) {
          setStats({
            ...stats,
            new: stats.new - 1,
            existing: stats.existing + 1
          });
        }
      } else {
        alert(data.error || 'Failed to add keyword');
      }
    } catch (err) {
      console.error('Error adding keyword:', err);
      alert('Failed to add keyword');
    } finally {
      setAddingKeywords(prev => {
        const next = new Set(prev);
        next.delete(keywordToAdd);
        return next;
      });
    }
  };

  const addAllKeywords = async () => {
    const newKeywords: Array<{keyword: string, languageCode: string}> = [];
    Object.values(translations).forEach(lang => {
      lang.keywords.forEach(k => {
        if (!k.isExisting && !addedKeywords.has(k.keyword)) {
          newKeywords.push({keyword: k.keyword, languageCode: k.languageCode});
        }
      });
    });

    if (newKeywords.length === 0) {
      alert('No new keywords to add');
      return;
    }

    if (!window.confirm(`Add ${newKeywords.length} new keywords?`)) {
      return;
    }

    for (const kw of newKeywords) {
      await addKeyword(kw.keyword, kw.languageCode);
    }

    onKeywordsAdded();
  };

  if (!isOpen) return null;

  return createPortal(
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 20000,
        backdropFilter: 'blur(4px)'
      }}
      onClick={onClose}
    >
      <div 
        style={{
          backgroundColor: 'var(--bg-primary)',
          borderRadius: '12px',
          padding: '24px',
          minWidth: '600px',
          maxWidth: '90vw',
          maxHeight: '85vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid var(--border-color)',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '20px',
          borderBottom: '1px solid var(--border-color)',
          paddingBottom: '12px'
        }}>
          <div>
            <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '20px' }}>
              🌐 Translations for: "{keyword}"
            </h3>
            {stats && !loading && (
              <p style={{ 
                margin: '8px 0 0 0', 
                fontSize: '14px', 
                color: 'var(--text-secondary)' 
              }}>
                {stats.total} translations • {stats.new} new • {stats.existing} existing
              </p>
            )}
          </div>
          <button
            className="btn btn-secondary"
            onClick={onClose}
            style={{ padding: '6px 12px' }}
          >
            ✕
          </button>
        </div>

        {/* Add All Button */}
        {!loading && stats && stats.new > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <button
              className="btn btn-primary"
              onClick={addAllKeywords}
              style={{ 
                padding: '8px 16px',
                width: '100%',
                fontWeight: 'bold'
              }}
            >
              Add All New Keywords ({stats.new} keywords)
            </button>
          </div>
        )}

        {/* Content */}
        <div style={{ 
          flex: 1, 
          overflowY: 'auto',
          paddingRight: '8px'
        }}>
          {loading && (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              minHeight: '200px'
            }}>
              <div className="spinner" />
            </div>
          )}

          {error && (
            <div style={{ 
              padding: '20px',
              backgroundColor: 'var(--error-bg)',
              color: 'var(--error-text)',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              {error}
            </div>
          )}

          {!loading && !error && Object.entries(translations).map(([language, data]) => (
            <div 
              key={language}
              style={{ 
                marginBottom: '20px',
                padding: '16px',
                backgroundColor: 'var(--bg-secondary)',
                borderRadius: '8px',
                border: '1px solid var(--border-color)'
              }}
            >
              <h4 style={{ 
                margin: '0 0 12px 0',
                color: 'var(--text-primary)',
                fontSize: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{ fontSize: '20px' }}>{data.flag}</span>
                <span>{data.name}</span>
              </h4>
              
              <div style={{ 
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px'
              }}>
                {data.keywords.map((kw) => (
                  <div
                    key={kw.keyword}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '6px 12px',
                      backgroundColor: 'var(--bg-primary)',
                      borderRadius: '6px',
                      border: '1px solid var(--border-color)'
                    }}
                  >
                    <span style={{ 
                      color: 'var(--text-primary)',
                      fontSize: '14px'
                    }}>
                      {kw.keyword}
                    </span>
                    
                    {kw.isExisting || addedKeywords.has(kw.keyword) ? (
                      <span style={{ 
                        color: '#4CAF50',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}>
                        ✓ Added
                      </span>
                    ) : (
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => addKeyword(kw.keyword, kw.languageCode)}
                        disabled={addingKeywords.has(kw.keyword)}
                        style={{ 
                          padding: '2px 8px',
                          fontSize: '12px',
                          minWidth: '50px'
                        }}
                      >
                        {addingKeywords.has(kw.keyword) ? '...' : 'Add'}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}

          {!loading && !error && Object.keys(translations).length === 0 && (
            <div style={{ 
              textAlign: 'center',
              padding: '40px',
              color: 'var(--text-secondary)'
            }}>
              No translations found
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default TranslatedKeywordsModal;